import { useState, type FormEvent } from 'react';
import { Button } from '@/design-system/ui/Button';
import { Card } from '@/design-system/ui/Card';
import { Input, Select, Textarea } from '@/design-system/ui/Field';
import { Icon } from '@/design-system/ui/Icon';
import { toApiError } from '@/core/api/errors';
import { toEnglishDigits } from '@/core/format/numbers';
import { isValidMobile } from '@/core/validation/validators';
import { useSEO } from '@/core/seo/useSEO';
import { submitComplaint, type ComplaintForm } from '@/features/support/api/support-api';
import { PageHeader } from '../components/PageHeader';

const CATEGORIES = [
  { value: 'delay', label: 'تأخیر در تحویل' },
  { value: 'damage', label: 'آسیب به مرسوله' },
  { value: 'behavior', label: 'رفتار نامناسب پیک' },
  { value: 'other', label: 'سایر موارد' },
];

const EMPTY: ComplaintForm = { loadCode: '', name: '', mobile: '', category: '', description: '' };
type Errors = Partial<Record<keyof ComplaintForm, string>>;

export default function ComplaintPage() {
  useSEO({
    title: 'ثبت شکایت و رسیدگی به امور مشتریان',
    description: 'سامانه ثبت شکایت و پیگیری انتقادات مسافران و صاحبان بار در بلینو برای رسیدگی فوری به تأخیر، خسارت یا مشکلات سفر.',
    canonical: '/complaint',
    keywords: 'شکایت بلینو, پشتیبانی بلینو, پیگیری خسارت بار, شکایات سفر بین شهری',
  });

  const [form, setForm] = useState<ComplaintForm>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [ticket, setTicket] = useState<string | null>(null);

  const set = (k: keyof ComplaintForm) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const next: Errors = {};
    if (!form.name.trim()) next.name = 'لطفاً نام خود را وارد کنید';
    if (!isValidMobile(form.mobile)) next.mobile = 'شماره موبایل معتبر نیست';
    if (!form.category) next.category = 'لطفاً موضوع شکایت را انتخاب کنید';
    if (form.description.trim().length < 10) next.description = 'لطفاً شرح شکایت را با جزئیات بیشتری بنویسید (حداقل ۱۰ نویسه)';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError('');
    try {
      const r = await submitComplaint({ ...form, loadCode: toEnglishDigits(form.loadCode.trim()), mobile: toEnglishDigits(form.mobile.trim()) });
      setTicket(r.ticketNumber ?? '—');
    } catch (err) {
      setServerError(toApiError(err).message || 'در ثبت شکایت مشکلی پیش آمد. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-8">
      <PageHeader icon="alert" title="ثبت شکایت" subtitle="شرح مشکل خود را ثبت کنید تا تیم پشتیبانی در سریع‌ترین زمان بررسی کند" />
      <Card className="p-5 sm:p-7">
        {ticket ? (
          <div role="status" className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="grid size-14 place-items-center rounded-full bg-success-light text-success"><Icon name="check" size={28} /></span>
            <h2 className="text-lg font-bold">شکایت شما با موفقیت ثبت شد</h2>
            <p className="text-sm text-ink-2">کد پیگیری شکایت: <strong className="text-ink">{ticket}</strong></p>
            <p className="text-xs text-ink-2">این کد را برای پیگیری‌های بعدی نزد خود نگه دارید.</p>
            <Button variant="outline" onClick={() => { setTicket(null); setForm(EMPTY); }}>ثبت شکایت جدید</Button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <p className="flex items-start gap-2 rounded-md bg-primary-light p-3 text-sm text-navy">
              <Icon name="info" size={18} className="mt-0.5 shrink-0" />
              با وارد کردن کد رهگیری مرسوله، شکایت شما سریع‌تر در دست بررسی قرار می‌گیرد.
            </p>
            {serverError && <p role="alert" className="rounded-md bg-danger-light p-3 text-sm font-medium text-danger">{serverError}</p>}
            <Input label="کد رهگیری (اختیاری)" inputMode="numeric" dir="ltr" className="text-start" value={form.loadCode} onChange={(e) => set('loadCode')(e.target.value)} disabled={loading} />
            <Input label="نام و نام خانوادگی" autoComplete="name" value={form.name} onChange={(e) => set('name')(e.target.value)} error={errors.name} disabled={loading} />
            <Input label="شماره موبایل" inputMode="numeric" autoComplete="tel" dir="ltr" className="text-start" maxLength={11} value={form.mobile} onChange={(e) => set('mobile')(toEnglishDigits(e.target.value).replace(/\D/g, ''))} error={errors.mobile} disabled={loading} />
            <Select label="موضوع شکایت" value={form.category} onChange={(e) => set('category')(e.target.value)} error={errors.category} disabled={loading}>
              <option value="">انتخاب کنید…</option>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
            <Textarea label="شرح شکایت" rows={5} maxLength={2000} value={form.description} onChange={(e) => set('description')(e.target.value)} error={errors.description} disabled={loading} />
            <Button type="submit" size="lg" fullWidth loading={loading} icon={<Icon name="send" size={20} />}>
              {loading ? 'در حال ثبت...' : 'ثبت شکایت'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
